import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import Chart from 'chart.js/auto';
import { ChartType } from 'chart.js';

@Component({
  selector: 'app-chart-js',
  standalone: true,
  template: `
    <div class="chart-js-wrapper" [style.height]="height" [style.width]="width">
      <canvas #chartCanvas></canvas>
    </div>
  `,
  styles: [`
    .chart-js-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
    }
    canvas {
      display: block;
      width: 100% !important;
      height: 100% !important;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartJsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() chartType: ChartType = 'bar';
  @Input() chartData: any = null;
  @Input() chartOptions: any = {};
  @Input() chartPlugins: any[] = [];
  @Input() height = '100%';
  @Input() width = '100%';

  @ViewChild('chartCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  private chart: Chart | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private viewInitDone = false;

  ngAfterViewInit(): void {
    this.viewInitDone = true;
    this.createChart();
    this.setupResizeObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If chart doesn't exist yet, try to create it when data arrives (after view init)
    if (!this.chart) {
      if (this.viewInitDone && this.chartData && this.canvasRef) {
        this.createChart();
        if (!this.resizeObserver) {
          this.setupResizeObserver();
        }
      }
      return;
    }

    // If chart type changed, we need to recreate
    if (changes['chartType'] && !changes['chartType'].firstChange) {
      this.destroyChart();
      this.createChart();
      return;
    }

    // If data or options changed, update in place
    if (changes['chartData'] && !changes['chartData'].firstChange) {
      if (this.chartData) {
        this.chart.data = this.chartData;
        this.chart.update('none'); // 'none' skips animations for faster updates
      }
      return;
    }

    if (changes['chartOptions'] && !changes['chartOptions'].firstChange) {
      this.chart.options = this.chartOptions as any;
      this.chart.update('none');
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.destroyChart();
  }

  private createChart(): void {
    if (!this.chartData || !this.canvasRef) return;

    this.chart = new Chart(this.canvasRef.nativeElement, {
      type: this.chartType,
      data: this.chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        ...this.chartOptions,
      },
      plugins: this.chartPlugins,
    });
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private setupResizeObserver(): void {
    const wrapper = this.canvasRef?.nativeElement?.parentElement;
    if (!wrapper) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      if (width === 0 || height === 0) return;

      if (this.chart) {
        // If chart was created in a hidden container (e.g. inactive mat-tab),
        // its canvas may have 0 dimensions. Recreate it now that it's visible.
        const canvas = this.chart.canvas;
        if (canvas && (canvas.clientWidth === 0 || canvas.clientHeight === 0)) {
          this.destroyChart();
          this.createChart();
        } else {
          this.chart.resize();
        }
      } else if (this.chartData) {
        // Chart wasn't created yet (data arrived while hidden), create now
        this.createChart();
      }
    });
    this.resizeObserver.observe(wrapper);
  }
}
